import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatTanggal, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Camera, MapPin, LogIn, LogOut, Clock, AlertTriangle,
  CheckCircle2, RotateCcw, ExternalLink,
} from "lucide-react";
import { distanceMeters, getCurrentPosition, compressImageDataUrl, fileToDataUrl, formatJam } from "@/lib/geo";

function StatusPill({ ok, children }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${ok ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
      {children}
    </span>
  );
}

function CheckActionDialog({ open, onOpenChange, mode, settings, onSubmit }) {
  const [photo, setPhoto] = useState(null);
  const [coords, setCoords] = useState(null);
  const [distance, setDistance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setPhoto(null);
      setCoords(null);
      setDistance(null);
      setError("");
    }
  }, [open]);

  const fetchLocation = useCallback(async () => {
    setError("");
    try {
      const pos = await getCurrentPosition();
      setCoords(pos);
      if (settings) {
        setDistance(distanceMeters(pos.lat, pos.lng, settings.office_lat, settings.office_lng));
      }
    } catch (e) {
      setError(e.message);
    }
  }, [settings]);

  useEffect(() => { if (open) fetchLocation(); }, [open, fetchLocation]);

  async function onFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const url = await fileToDataUrl(f);
      const compressed = await compressImageDataUrl(url, 720, 0.7);
      setPhoto(compressed);
    } catch (err) {
      setError("Gagal memuat foto");
    }
  }

  async function submit() {
    if (!photo) return toast.error("Ambil foto terlebih dahulu");
    if (!coords) return toast.error("Lokasi belum terdeteksi");
    setLoading(true);
    try {
      await onSubmit({ photo, lat: coords.lat, lng: coords.lng });
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }

  const inRadius = settings && distance !== null && distance <= settings.office_radius_m;
  const titleColor = mode === "in" ? "from-emerald-500 to-emerald-600" : "from-amber-500 to-amber-600";
  const verb = mode === "in" ? "Check-In Datang" : "Check-Out Pulang";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={`dialog-${mode === "in" ? "checkin" : "checkout"}`}>
        <div className={`-mt-6 -mx-6 mb-2 rounded-t-lg bg-gradient-to-r ${titleColor} px-6 py-4 text-white`}>
          <DialogHeader>
            <DialogTitle className="text-white">{verb}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-white/80 mt-1">Pastikan Anda berada di area kantor & ambil foto selfie.</p>
        </div>

        {/* Location panel */}
        <div className="rounded-md border border-border p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4 text-brand" />
              <div>
                <div className="font-medium text-ink-900">{settings?.office_name}</div>
                <div className="text-xs text-ink-500">Radius maks {settings?.office_radius_m} m</div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={fetchLocation} data-testid="refresh-location">
              <RotateCcw className="mr-1 h-3 w-3" /> Refresh
            </Button>
          </div>
          {coords ? (
            <div className="mt-3 flex items-center justify-between">
              <StatusPill ok={inRadius}>
                {inRadius
                  ? `Dalam area (~${Math.round(distance)} m)`
                  : `Di luar area (~${Math.round(distance)} m)`}
              </StatusPill>
              <a
                href={`https://www.google.com/maps?q=${coords.lat},${coords.lng}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-brand hover:underline"
              >
                Lihat di Maps <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          ) : (
            <div className="mt-3 text-xs text-ink-500">Mendeteksi lokasi…</div>
          )}
        </div>

        {/* Photo */}
        <div className="rounded-md border border-dashed border-border p-3">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="user"
            className="hidden"
            onChange={onFileChange}
            data-testid="photo-input"
          />
          {photo ? (
            <div className="space-y-2">
              <img src={photo} alt="preview" className="mx-auto max-h-48 rounded-md object-cover" />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="w-full" data-testid="retake-photo">
                <Camera className="mr-2 h-4 w-4" /> Ambil ulang
              </Button>
            </div>
          ) : (
            <Button variant="outline" onClick={() => fileRef.current?.click()} className="w-full" data-testid="take-photo">
              <Camera className="mr-2 h-4 w-4" /> Buka kamera / pilih foto
            </Button>
          )}
        </div>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">{error}</div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button
            onClick={submit}
            disabled={loading || !photo || !coords}
            className={`${mode === "in" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"} text-white`}
            data-testid={`submit-${mode === "in" ? "checkin" : "checkout"}`}
          >
            {mode === "in" ? <LogIn className="mr-2 h-4 w-4" /> : <LogOut className="mr-2 h-4 w-4" />}
            {loading ? "Mengirim…" : verb}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function KaryawanDashboard() {
  const { user } = useAuth();
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [openIn, setOpenIn] = useState(false);
  const [openOut, setOpenOut] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [t, h] = await Promise.all([api.get("/attendance/today"), api.get("/attendance")]);
      setToday(t.data);
      setHistory(h.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function doCheckIn(payload) {
    try {
      await api.post("/attendance/check-in", payload);
      toast.success("Berhasil check-in. Selamat bekerja!");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  }
  async function doCheckOut(payload) {
    try {
      await api.post("/attendance/check-out", payload);
      toast.success("Berhasil check-out. Sampai jumpa!");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  }

  const rec = today?.record;
  const checkedIn = !!rec?.check_in_at;
  const checkedOut = !!rec?.check_out_at;
  const settings = today?.settings;

  return (
    <div className="space-y-6" data-testid="karyawan-dashboard">
      <PageHeader
        overline="Absensi karyawan"
        title={`Halo, ${user?.name?.split(" ")[0] || user?.username}!`}
        description={`Hari ini ${formatTanggal(today?.today)}. Jam kerja: ${settings?.work_start || "—"} – ${settings?.work_end || "—"} WIB.`}
        icon={Clock}
        tone="success"
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Check-In */}
        <Card className={`relative overflow-hidden border shadow-none ${checkedIn ? "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" : "bg-card"}`}>
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 to-emerald-300" />
          <div className="p-6">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-md bg-emerald-500 text-white shadow-sm">
                <LogIn className="h-5 w-5" />
              </div>
              <div>
                <p className="overline text-emerald-700">Datang</p>
                <h2 className="font-heading text-xl font-bold tracking-tight text-emerald-900">
                  {checkedIn ? formatJam(rec.check_in_at) : "Belum check-in"}
                </h2>
              </div>
            </div>
            {checkedIn && (
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <StatusPill ok={!rec.check_in_late}>{rec.check_in_late ? "Terlambat" : "Tepat waktu"}</StatusPill>
                <span className="rounded-full bg-white/70 px-2 py-1 text-ink-500">±{Math.round(rec.check_in_distance_m)} m dari kantor</span>
              </div>
            )}
            <Button
              onClick={() => setOpenIn(true)}
              disabled={checkedIn}
              className="mt-4 w-full bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="btn-checkin"
            >
              <Camera className="mr-2 h-4 w-4" /> {checkedIn ? "Sudah Check-In" : "Check-In dengan Foto"}
            </Button>
          </div>
        </Card>

        {/* Check-Out */}
        <Card className={`relative overflow-hidden border shadow-none ${checkedOut ? "border-amber-200 bg-gradient-to-br from-amber-50 to-white" : "bg-card"}`}>
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-500 to-amber-300" />
          <div className="p-6">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-md bg-amber-500 text-white shadow-sm">
                <LogOut className="h-5 w-5" />
              </div>
              <div>
                <p className="overline text-amber-700">Pulang</p>
                <h2 className="font-heading text-xl font-bold tracking-tight text-amber-900">
                  {checkedOut ? formatJam(rec.check_out_at) : (checkedIn ? "Belum check-out" : "—")}
                </h2>
              </div>
            </div>
            {checkedOut && (
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <StatusPill ok={!rec.check_out_early}>{rec.check_out_early ? "Pulang lebih awal" : "Sesuai jam"}</StatusPill>
                <span className="rounded-full bg-white/70 px-2 py-1 text-ink-500">±{Math.round(rec.check_out_distance_m)} m dari kantor</span>
              </div>
            )}
            <Button
              onClick={() => setOpenOut(true)}
              disabled={!checkedIn || checkedOut}
              className="mt-4 w-full bg-amber-600 text-white hover:bg-amber-700"
              data-testid="btn-checkout"
            >
              <Camera className="mr-2 h-4 w-4" /> {checkedOut ? "Sudah Check-Out" : "Check-Out dengan Foto"}
            </Button>
          </div>
        </Card>
      </div>

      {/* History */}
      <section>
        <div className="mb-3">
          <p className="overline">Riwayat</p>
          <h2 className="mt-1 font-heading text-xl font-semibold tracking-tight">Absensi sebelumnya</h2>
        </div>
        <Card className="overflow-hidden border-border shadow-none">
          <div className="divide-y divide-border">
            {history.length === 0 && (
              <div className="p-10 text-center text-sm text-ink-500">Belum ada riwayat absensi.</div>
            )}
            {history.map((h) => (
              <div key={h.id} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="font-medium text-ink-900">{formatTanggal(h.date)}</div>
                  <div className="text-xs text-ink-500">
                    Datang {formatJam(h.check_in_at)} · Pulang {formatJam(h.check_out_at)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {h.check_in_late ? (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Terlambat</Badge>
                  ) : h.check_in_at ? (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Tepat waktu</Badge>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </section>

      <CheckActionDialog open={openIn} onOpenChange={setOpenIn} mode="in" settings={settings} onSubmit={doCheckIn} />
      <CheckActionDialog open={openOut} onOpenChange={setOpenOut} mode="out" settings={settings} onSubmit={doCheckOut} />
    </div>
  );
}
